<template>
  <div v-if="$apollo.loading">
    <spinner />
  </div>
  <div v-else class="page-wrapper">
    <div class="title-screen">
      <span>{{ adminText('organizationAll.ticket_categories_6e86252d', 'Ticket categories') }}</span>
    </div>
    <p class="page-description">
      {{ adminText('organizationAll.here_you_can_add_and_manage_your_ticket_categ_2c9d56fa', 'Here you can add and manage your ticket categories.') }}<br>{{ adminText('organizationAll.to_create_view_or_edit_tickets_open_the_event_f4134883', 'To create, view or edit tickets, open the event dashboard.') }}
    </p>
    <main-button style="max-width: 244px; margin-bottom: 1rem" @click="handleOpenCreateModal">
      <span class="add-symbol">+</span>{{ adminText('organizationAll.create_category_61495a81', 'Create category') }}
    </main-button>
    <div v-if="categories.length" class="attendee_table">
      <table-list
        :event-id="null"
        :selected.sync="selected"
        :hide-checkbox="true"
        :organization-slug="organizationSlug"
        :attendees="categories"
        body-style="grid-auto-columns: 50px minmax(50px, 150px) !important;"
        :visible-fields="visibleFields"
        :attendee-fields="attendeeFields"
        :pagination="pagination"
        @click="handleCategoryClick($event)"
      />
      <table-pagination
        :pagination="pagination"
        :total-attendees="totalCategoriesCount"
        @change="handlePagination($event)"
        @changePerPage="handlePagination($event)"
      />
    </div>
    <manage-ticket-categories-modal
      v-if="openModal"
      :show.sync="openModal"
      :initial-values="form"
      :colors="colors"
      :priorities="priorities"
      :categories="categories"
      @values="form = $event"
      @create-category="handleCreateCategory($event)"
      @close="closeDialog"
      @delete="handleDelete($event)"
      @update="handleUpdate($event)"
      @warning-message="handleWarningMessage($event)"
    />
    <simple-snackbar v-bind.sync="snackbar" :message-prop="organizationText(snackbar &amp;&amp; (snackbar.messageProp || snackbar.message))" />
  </div>
</template>
<script>
import OrganizationLocalization from '@/components/organizations/organization-all-display'

import tableList from '@/components/accreditations/attendee-table/list.vue'
import tablePagination from '@/components/accreditations/attendee-table/pagination.vue'
import ManageTicketCategoriesModal from '@/components/organizations/ticket-categories/ManageTicketCategoriesModal.vue'
import simpleSnackbar from '@/components/shared/snackbars/simple-snackbar.vue'
import {
  CREATE_ORGANIZATION_ITEM_CATEGORY,
  DELETE_ORGANIZATION_ITEM_CATEGORY,
  GET_ORGANIZATION_ITEM_CATEGORIES,
  UPDATE_ORGANIZATION_ITEM_CATEGORY_BY_ID,
} from '@/graphql/organizationItemCategory.graphql'

export const CategoryColors = [
  {
    name: 'CoreEvent',
    hexadecimal: '#0277BD',
  },
  {
    name: 'Blue',
    hexadecimal: '#0220BD',
  },
  {
    name: 'Violet',
    hexadecimal: '#6B02BD',
  },
  {
    name: 'Purple',
    hexadecimal: '#B902BD',
  },
  {
    name: 'Red',
    hexadecimal: '#BD0202',
  },
  {
    name: 'Orange',
    hexadecimal: '#BD5C02',
  },
  {
    name: 'Yellow',
    hexadecimal: '#988A14',
  },
  {
    name: 'Green',
    hexadecimal: '#0E7E1A',
  },
  {
    name: 'Aqua',
    hexadecimal: '#0F8890',
  },
]

export const Priorities = [
  {
    decimal: 1,
    roman: 'I',
  },
  {
    decimal: 2,
    roman: 'II',
  },
  {
    decimal: 3,
    roman: 'III',
  },
  {
    decimal: 4,
    roman: 'IV',
  },
  {
    decimal: 5,
    roman: 'V',
  },
  {
    decimal: 6,
    roman: 'VI',
  },
  {
    decimal: 7,
    roman: 'VII',
  },
  {
    decimal: 8,
    roman: 'I',
  },
  {
    decimal: 9,
    roman: 'IX',
  },
  {
    decimal: 10,
    roman: 'X',
  },
]

export default {
  mixins: [OrganizationLocalization],
  name: 'tickets-categories',
  components: { simpleSnackbar, ManageTicketCategoriesModal, tableList, tablePagination },
  props: {
    eventId: String,
    organizationSlug: String,
  },
  data() {
    const { pagination } = this.getInitialFromQueryParams()

    return {
      totalCategoriesCount: 0,
      categories: [],
      visibleFields: ['Ticket category name', 'Color', 'Priority'],
      attendeeFields: {},
      pagination,
      openModal: false,
      selected: [],
      form: {},
      colors: CategoryColors,
      priorities: Priorities.map(p => p.roman),
      snackbar: {},
    }
  },
  apollo: {
    getCategories: {
      query: GET_ORGANIZATION_ITEM_CATEGORIES,
      manual: true,
      variables() {
        return {
          organizationSlug: this.organizationSlug,
          pagination: {
            ...this.pagination,
            per_page: this.pagination.per_page === 'All' ? -1 : this.pagination.per_page,
          },
        }
      },
      result({ data: { getOrganizationItemCategories }, loading }) {
        if (!loading) {
          this.categories = getOrganizationItemCategories.categories.map(category => {
            return {
              ...category,
              color: {
                name: this.getColorNameFromHexadecimal(category.color),
                hexadecimal: category.color,
              },
              priority: this.getRomanNumeralFromDecimal(category.priority),
            }
          })
          this.totalCategoriesCount = getOrganizationItemCategories.count
        }
      },
    },
  },

  methods: {
    async handleCategoryClick(categoryId) {
      const category = this.categories.find(c => parseInt(c.id) === categoryId)
      this.form = category
      this.openModal = !this.openModal
    },
    async handleDelete(form) {
      const { errors } = await this.$apollo.mutate({
        mutation: DELETE_ORGANIZATION_ITEM_CATEGORY,
        variables: {
          organizationSlug: this.organizationSlug,
          id: form.id,
        },
      })
      if (!errors) {
        await this.$apollo.queries.getCategories.refetch()
        this.closeDialog()
      }
    },
    getInitialFromQueryParams() {
      const { per_page, page } = this.$route.query

      return {
        pagination: {
          page: parseInt(page, 10) || 1,
          per_page: parseInt(per_page, 10) || 'All',
        },
      }
    },
    async handlePagination({ page, per_page }) {
      const { page: currentPage, per_page: currentPerPage } = this.pagination
      if (currentPage > page && currentPage === 1) return
      if (page > currentPage && !(this.totalCategoriesCount - currentPerPage * currentPage > 0))
        return

      this.pagination = {
        ...this.pagination,
        per_page: per_page,
        page: page,
      }

      await this.addPaginationToRoute(this.pagination)
    },
    async addPaginationToRoute({ page, per_page }) {
      await this.$router.replace({
        query: {
          ...this.$route.query,
          per_page,
          page,
        },
      })
    },
    getColorNameFromHexadecimal(hex) {
      for (const color of CategoryColors) {
        if (color.hexadecimal === hex.toUpperCase()) return color.name
      }

      return ''
    },
    getRomanNumeralFromDecimal(decimal) {
      for (const number of Priorities) {
        if (number.decimal === decimal) return number.roman
      }

      return ''
    },
    getDecimalFromRomanNumber(roman) {
      for (const number of Priorities) {
        if (number.roman === roman.toUpperCase()) return number.decimal
      }

      return 0
    },
    handleOpenCreateModal() {
      this.openModal = !this.openModal
    },
    handleWarningMessage(message) {
      this.snackbar = {
        open: true,
        messageProp: message,
        color: 'error',
      }
    },
    async handleUpdate(form) {
      const { errors } = await this.$apollo.mutate({
        mutation: UPDATE_ORGANIZATION_ITEM_CATEGORY_BY_ID,
        awaitRefetchQueries: true,
        refetchQueries: ['GetOrganizationItemCategories'],
        variables: {
          organizationSlug: this.organizationSlug,
          ...form,
          color: form.color.hexadecimal,
          priority: this.getDecimalFromRomanNumber(form.priority),
        },
      })
      if (errors) {
        this.snackbar = {
          open: true,
          messageProp: errors[0].message,
          color: 'error',
        }
      } else {
        this.snackbar = {
          open: true,
          messageProp: 'Category successfully updated!',
          color: 'success',
        }
        this.closeDialog()
      }
    },
    closeDialog() {
      this.openModal = false
      this.form = {
        displayIfSoldOut: false,
      }
    },
    async handleCreateCategory(form) {
      const { errors } = await this.$apollo.mutate({
        mutation: CREATE_ORGANIZATION_ITEM_CATEGORY,
        awaitRefetchQueries: true,
        refetchQueries: ['GetOrganizationItemCategories'],
        variables: {
          organizationSlug: this.organizationSlug,
          ...form,
          color: form.color.hexadecimal,
          priority: this.getDecimalFromRomanNumber(form.priority),
        },
      })

      if (errors) {
        this.snackbar = {
          open: true,
          messageProp: errors[0].message,
          color: 'error',
        }
      } else {
        this.snackbar = {
          open: true,
          messageProp: 'Category successfully created!',
          color: 'success',
        }
        this.closeDialog()
      }
    },
  },
}
</script>

<style scoped>
.page-description {
  font-family: 'Source Sans Pro';
  font-style: normal;
  font-weight: 400;
  font-size: 1rem;
  line-height: 150%;
  color: #979797;
}
@media screen and (max-width: 800px) {
  .page-wrapper {
    margin-top: 1.5rem;
  }
}
</style>
